
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  HiOutlineArrowUpRight,
  HiOutlineScale,
  HiOutlineShieldCheck,
  HiOutlineBriefcase,
  HiOutlineBuildingOffice2,
  HiOutlineBuildingLibrary,
  HiOutlineUserGroup,
  HiOutlineDocumentText,
  HiOutlineHomeModern,
} from "react-icons/hi2";

import {
  FaGavel,
  FaHandshake,
} from "react-icons/fa";

import { media } from "../../server";

/* ============================================
   SERVICE ICON REGISTRY

   Supports icon names stored in MongoDB.
============================================ */

const serviceIcons = {
  scale: HiOutlineScale,
  gavel: FaGavel,
  handshake: FaHandshake,

  "shield-check": HiOutlineShieldCheck,
  shield: HiOutlineShieldCheck,

  briefcase: HiOutlineBriefcase,
  "briefcase-business": HiOutlineBriefcase,

  building: HiOutlineBuildingOffice2,
  "building-2": HiOutlineBuildingOffice2,

  landmark: HiOutlineBuildingLibrary,

  users: HiOutlineUserGroup,
  "users-round": HiOutlineUserGroup,

  "file-text": HiOutlineDocumentText,
  file: HiOutlineDocumentText,

  house: HiOutlineHomeModern,
  home: HiOutlineHomeModern,

  "heart-handshake": FaHandshake,
};

/* ============================================
   IMAGE URL HELPER
============================================ */

function getImageUrl(image) {
  if (!image || typeof image !== "string") {
    return null;
  }

  const filename = image.trim();

  if (!filename) return null;

  // Already a full image URL
  if (
    /^https?:\/\//i.test(filename) ||
    /^data:image\//i.test(filename)
  ) {
    return filename;
  }

  // Image served from the backend media directory
  const base = String(media || "").replace(/\/+$/, "");
  const path = filename.replace(/^\/+/, "");

  return base
    ? `${base}/${path}`
    : `/${path}`;
}

/* ============================================
   REMOVE HTML FROM SHORT DESCRIPTION
============================================ */

function getPlainText(value) {
  if (typeof value !== "string") return "";

  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/* ============================================
   SERVICE CARD
============================================ */

function ServiceCard({ service, index = 0 }) {
  const { i18n } = useTranslation();

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const title = isEnglish
    ? service.titleEn
    : service.titleAr;

  const description = getPlainText(
    isEnglish
      ? service.textEn
      : service.textAr
  );

  const readMore = isEnglish
    ? "Explore Service"
    : "تعرف على الخدمة";

  const iconName = String(service.icon || "scale")
    .trim()
    .toLowerCase();

  const Icon = serviceIcons[iconName] || HiOutlineScale;

  const imageUrl = getImageUrl(service.Image);

  const [imageError, setImageError] = useState(false);

  // Reset the image state when service changes
  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const showImage = Boolean(imageUrl) && !imageError;

  return (
    <article
      className="
        group relative
        flex h-full min-w-0
        flex-col overflow-hidden

        rounded-[22px]

        border border-[#DAE4EB]
        bg-white

        shadow-[0_10px_35px_rgba(13,35,55,0.045)]

        transition-all duration-300
        ease-out

        hover:-translate-y-1.5
        hover:border-[#C19B60]/40
        hover:shadow-[0_22px_50px_rgba(13,35,55,0.11)]

        dark:border-white/10
        dark:bg-[#1B3549]
        dark:shadow-none

        dark:hover:border-[#D7B77F]/30
        dark:hover:shadow-[0_18px_45px_rgba(0,0,0,0.12)]
      "
    >
      {/* ======================================
          IMAGE AREA
      ====================================== */}

      <div
        className="
          relative
          aspect-[16/10]
          w-full
          overflow-hidden

          bg-[#172F43]
        "
      >

        {showImage ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
            className="
              absolute inset-0
              h-full w-full

              object-cover
              object-center

              transition-transform
              duration-700
              ease-out

              group-hover:scale-[1.055]
            "
          />
        ) : (
          /* Elegant fallback if image is missing */
          <div
            className="
              absolute inset-0
              flex items-center justify-center

              bg-gradient-to-br
              from-[#25445D]
              via-[#152D42]
              to-[#0B1726]
            "
          >
            {/* Decorative circles */}
            <div
              aria-hidden="true"
              className="
                absolute -end-16 -top-20
                h-64 w-64
                rounded-full
                border border-white/10
              "
            />

            <div
              aria-hidden="true"
              className="
                absolute -start-20 -bottom-24
                h-56 w-56
                rounded-full
                border border-white/10
              "
            />

            <Icon
              size={72}
              aria-hidden="true"
              className="
                relative z-10
                text-[#D7B77F]/50
              "
            />
          </div>
        )}

        {/* Dark gradient over the image */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute inset-0

            bg-gradient-to-t
            from-[#081827]/65
            via-transparent
            to-[#081827]/15
          "
        />

        {/* ==================================
            SERVICE NUMBER
        ================================== */}

        <div
          className="
            absolute end-4 top-4

            inline-flex min-h-9
            items-center justify-center

            rounded-lg
            border border-white/20
            bg-[#102031]/40
            px-3

            text-white
            backdrop-blur-md
          "
        >
          <span
            className="
              caption font-semibold
              tabular-nums
            "
          >
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>

        {/* ==================================
            SERVICE ICON
        ================================== */}

        <div
          className="
            absolute bottom-4 start-5

            flex h-12 w-12
            items-center justify-center

            rounded-xl
            border border-white/20
            bg-[#102031]/75

            text-[#D7B77F]

            shadow-lg
            backdrop-blur-lg

            transition-all duration-300

            group-hover:border-[#D7B77F]/40
            group-hover:bg-[#102031]/90
          "
        >
          <Icon
            size={23}
            aria-hidden="true"
          />
        </div>

      </div>

      {/* ======================================
          CARD CONTENT
      ====================================== */}

      <div
        className="
          flex flex-1
          flex-col

          px-6 pb-6 pt-7
          sm:px-7 sm:pb-7
        "
      >

        {/* Eyebrow */}
        <div className="mb-4 flex items-center gap-3">

          <span
            aria-hidden="true"
            className="
              h-px w-7
              bg-skin-accent
            "
          />

          <span
            className="
              eyebrow
              text-skin-accent
            "
          >
            {isEnglish
              ? "Legal Practice"
              : "الممارسات القانونية"}
          </span>

        </div>

        {/* Service title */}
        <h3
          className="
            card-title
            text-skin-heading
            transition-colors duration-300

            group-hover:text-brand-navyLight

            dark:group-hover:text-white
          "
        >
          {title}
        </h3>

        {/* Service description */}
        <p
          className="
            card-description
            mt-4

            line-clamp-3
            min-h-[76px]

            text-skin-muted
          "
        >
          {description}
        </p>

        {/* ==================================
            FOOTER ACTION
        ================================== */}

        <div
          className="
            mt-auto pt-7
          "
        >
          <div
            className="
              mb-5 h-px w-full
              bg-skin-border
            "
          />

          <Link
            to="/ServiceDetails"
            state={{ ServiceData: service }}
            onClick={() => {
              window.scrollTo(0, 0);
            }}
            aria-label={`${readMore}: ${title}`}
            className="
              group/link
              inline-flex w-full
              items-center justify-between
              gap-4

              rounded-lg
              text-skin-heading

              transition-colors duration-300
              hover:text-skin-accent

              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-skin-accent
            "
          >
            {/* Link text */}
            <span
              className="
                button-sm
                font-semibold
              "
            >
              {readMore}
            </span>

            {/* Arrow icon */}
            <span
              className="
                flex h-10 w-10
                shrink-0 items-center
                justify-center

                rounded-xl
                border border-skin-border
                bg-skin-soft

                text-skin-heading

                transition-all duration-300

                group-hover/link:border-brand-navy
                group-hover/link:bg-brand-navy
                group-hover/link:text-white

                dark:group-hover/link:border-white/20
                dark:group-hover/link:bg-white/15
              "
            >
              <HiOutlineArrowUpRight
                size={19}
                aria-hidden="true"
                className="directional-arrow"
              />
            </span>

          </Link>
        </div>
      </div>

    </article>
  );
}

export default ServiceCard;
